import scoutAirframeJson from '../../config/drone.scout.json';
import fiveInchAirframeJson from '../../config/drone.5inch.json';
import ga5000RadarJson from '../../config/radar.ga5000.json';
import { parseCompanionConfig, type CompanionConfig } from '../companion/companionConfig';
import type { Geodetic, Vec3 } from './mathx';
import { parseRadarConfig, type RadarConfig } from './radarConfig';
import { parseTargetSpec, type TargetSpec } from './targetTypes';
import { parseVehicleConfig, type VehicleConfig } from './vehicleConfig';

export interface ScenarioSuccess {
  reactionTimeS: number;
  stationErrorM: number;
  targetInFrustumPct: number;
  minSeparationM: number;
  trackContinuityPct: number;
}

export interface SceneSpec {
  id: string;
  /** Collision asset reference. `builtin:ground` is available before ticket 20. */
  staticGeometry: string;
}

export interface ScoutScenarioSpec {
  airframe: string;
  vehicleConfig: VehicleConfig;
  startEnu: Vec3;
  homeEnu: Vec3;
}

/** Fully validated and resolved scenario used by the runtime. */
export interface ScenarioSpec {
  name: string;
  originLatLonAlt: Geodetic;
  scene: SceneSpec;
  radar: RadarConfig;
  targets: TargetSpec[];
  scout: ScoutScenarioSpec;
  companion: CompanionConfig;
  wind: Vec3;
  success: ScenarioSuccess;
  seed: number;
  maxSimTime: number;
}

export class ScenarioSchemaError extends Error {
  constructor(
    readonly path: string,
    message: string,
  ) {
    super(`scenario: ${path} ${message}`);
    this.name = 'ScenarioSchemaError';
  }
}

const AIRFRAMES: Readonly<Record<string, unknown>> = Object.freeze({
  'config/drone.scout.json': scoutAirframeJson,
  'config/drone.5inch.json': fiveInchAirframeJson,
});

const RADARS: Readonly<Record<string, unknown>> = Object.freeze({
  'config/radar.ga5000.json': ga5000RadarJson,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const objectAt = (value: unknown, path: string): Record<string, unknown> => {
  if (!isRecord(value)) throw new ScenarioSchemaError(path, 'must be an object');
  return value;
};

const finiteAt = (value: unknown, path: string): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new ScenarioSchemaError(path, 'must be a finite number');
  }
  return value;
};

const positiveAt = (value: unknown, path: string): number => {
  const parsed = finiteAt(value, path);
  if (parsed <= 0) throw new ScenarioSchemaError(path, 'must be > 0');
  return parsed;
};

const stringAt = (value: unknown, path: string): string => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new ScenarioSchemaError(path, 'must be a non-empty string');
  }
  return value;
};

const vec3At = (value: unknown, path: string): Vec3 => {
  const raw = objectAt(value, path);
  return {
    x: finiteAt(raw.x, `${path}.x`),
    y: finiteAt(raw.y, `${path}.y`),
    z: finiteAt(raw.z, `${path}.z`),
  };
};

const originAt = (value: unknown): Geodetic => {
  const raw = objectAt(value, 'originLatLonAlt');
  const lat = finiteAt(raw.lat, 'originLatLonAlt.lat');
  const lon = finiteAt(raw.lon, 'originLatLonAlt.lon');
  if (lat < -90 || lat > 90) {
    throw new ScenarioSchemaError('originLatLonAlt.lat', 'must be between -90 and 90');
  }
  if (lon < -180 || lon > 180) {
    throw new ScenarioSchemaError('originLatLonAlt.lon', 'must be between -180 and 180');
  }
  return { lat, lon, alt: finiteAt(raw.alt, 'originLatLonAlt.alt') };
};

const mergeObjects = (
  base: Record<string, unknown>,
  overrides: Record<string, unknown>,
): Record<string, unknown> => {
  const merged: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    const baseValue = merged[key];
    merged[key] = isRecord(baseValue) && isRecord(value) ? mergeObjects(baseValue, value) : value;
  }
  return merged;
};

const radarAt = (value: unknown): RadarConfig => {
  let path = 'config/radar.ga5000.json';
  let overrides: Record<string, unknown> = {};
  if (typeof value === 'string') {
    path = value;
  } else {
    const raw = objectAt(value, 'radar');
    const configuredPath = raw.path ?? raw.config;
    if (configuredPath !== undefined) path = stringAt(configuredPath, 'radar.path');
    if (raw.overrides !== undefined) {
      overrides = objectAt(raw.overrides, 'radar.overrides');
    } else {
      overrides = Object.fromEntries(
        Object.entries(raw).filter(([key]) => key !== 'path' && key !== 'config'),
      );
    }
  }
  const base = RADARS[path];
  if (!base) throw new ScenarioSchemaError('radar.path', `does not exist: ${path}`);
  try {
    return parseRadarConfig(mergeObjects(objectAt(base, 'radar'), overrides));
  } catch (error) {
    throw new ScenarioSchemaError(
      'radar',
      error instanceof Error ? error.message : 'contains invalid configuration',
    );
  }
};

const successAt = (value: unknown): ScenarioSuccess => {
  const raw = objectAt(value, 'success');
  const percentage = (key: 'targetInFrustumPct' | 'trackContinuityPct'): number => {
    const parsed = finiteAt(raw[key], `success.${key}`);
    if (parsed < 0 || parsed > 100) {
      throw new ScenarioSchemaError(`success.${key}`, 'must be between 0 and 100');
    }
    return parsed;
  };
  return {
    reactionTimeS: positiveAt(raw.reactionTimeS, 'success.reactionTimeS'),
    stationErrorM: positiveAt(raw.stationErrorM, 'success.stationErrorM'),
    targetInFrustumPct: percentage('targetInFrustumPct'),
    minSeparationM: positiveAt(raw.minSeparationM, 'success.minSeparationM'),
    trackContinuityPct: percentage('trackContinuityPct'),
  };
};

/** Hand-rolled validation plus resolution of the two Phase-1 config references. */
export const parseScenario = (value: unknown): ScenarioSpec => {
  const raw = objectAt(value, 'root');
  const sceneRaw = objectAt(raw.scene, 'scene');
  const scoutRaw = objectAt(raw.scout, 'scout');
  const airframe = stringAt(scoutRaw.airframe, 'scout.airframe');
  const airframeJson = AIRFRAMES[airframe];
  if (!airframeJson) {
    throw new ScenarioSchemaError('scout.airframe', `does not exist: ${airframe}`);
  }
  if (!Array.isArray(raw.targets)) {
    throw new ScenarioSchemaError('targets', 'must be an array');
  }
  const seed = finiteAt(raw.seed, 'seed');
  if (!Number.isSafeInteger(seed)) throw new ScenarioSchemaError('seed', 'must be a safe integer');

  const radar = radarAt(raw.radar);
  let companion: CompanionConfig;
  try {
    companion = parseCompanionConfig({
      radarEnu: radar.mountEnu,
      ...objectAt(raw.companion, 'companion'),
    });
  } catch (error) {
    throw new ScenarioSchemaError(
      'companion',
      error instanceof Error ? error.message : 'contains invalid configuration',
    );
  }

  return {
    name: stringAt(raw.name, 'name'),
    originLatLonAlt: originAt(raw.originLatLonAlt),
    scene: {
      id: stringAt(sceneRaw.id, 'scene.id'),
      staticGeometry: stringAt(
        sceneRaw.staticGeometry ?? sceneRaw.staticGeometryRef,
        'scene.staticGeometry',
      ),
    },
    radar,
    targets: raw.targets.map((target, index) => {
      try {
        return parseTargetSpec(target, `targets[${index}]`);
      } catch (error) {
        throw new ScenarioSchemaError(
          `targets[${index}]`,
          error instanceof Error ? error.message : 'is invalid',
        );
      }
    }),
    scout: {
      airframe,
      vehicleConfig: parseVehicleConfig(airframeJson),
      startEnu: vec3At(scoutRaw.startEnu, 'scout.startEnu'),
      homeEnu: vec3At(scoutRaw.homeEnu, 'scout.homeEnu'),
    },
    companion,
    wind: raw.wind === undefined ? { x: 0, y: 0, z: 0 } : vec3At(raw.wind, 'wind'),
    success: successAt(raw.success),
    seed,
    maxSimTime: positiveAt(raw.maxSimTime, 'maxSimTime'),
  };
};
